/**
 * 机上調査の結果の読み取りに使う項目 (説明文・所見) の入力欄 (docs/27 §17-3)。
 * 項目名を推定しないため、ここで選ぶ。空欄は地区ではテンプレートに、テンプレートでは既定に従う。
 */
import type { AiOutputFormatSchema, StreetSurveyResultFieldsDef } from '@citizen-walk/shared-types';
import {
  listResultFieldOptions,
  type StreetSurveyResultFieldsDraft,
} from './streetSurveyResultFieldsDraft';

export interface StreetSurveyResultFieldsFormProps {
  schema: AiOutputFormatSchema | null;
  draft: StreetSurveyResultFieldsDraft;
  onChange: (next: StreetSurveyResultFieldsDraft) => void;
  /** 引き継ぐ元 (地区ではテンプレートの設定)。 */
  inheritedDef?: StreetSurveyResultFieldsDef | null;
  scope: 'district' | 'template';
  disabled?: boolean;
}

const selectClass =
  'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

export function StreetSurveyResultFieldsForm({
  schema,
  draft,
  onChange,
  inheritedDef,
  scope,
  disabled = false,
}: StreetSurveyResultFieldsFormProps) {
  const options = listResultFieldOptions(schema);
  const labelOf = (key: string | null | undefined) =>
    key ? (options.find((o) => o.key === key)?.label ?? key) : null;
  const inheritDescription =
    scope === 'district'
      ? `テンプレートに従う（${labelOf(inheritedDef?.descriptionKey) ?? '新方式の説明文のみ'}）`
      : '既定（新方式の説明文のみ）';
  const inheritInsight =
    scope === 'district'
      ? `テンプレートに従う（${labelOf(inheritedDef?.insightKey) ?? '表示しない'}）`
      : '既定（表示しない）';
  // 保存値が候補に無い (出力フォーマットが違う) ときも選択状態を保つ。
  const withCurrent = (key: string) =>
    key && !options.some((o) => o.key === key)
      ? [...options, { key, label: `${key}（この出力フォーマットにありません）` }]
      : options;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="block text-xs text-slate-600 min-w-0">
        説明文として表示する項目
        <select
          className={selectClass}
          style={{ maxWidth: '100%' }}
          value={draft.descriptionKey}
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, descriptionKey: e.target.value })}
        >
          <option value="">{inheritDescription}</option>
          {withCurrent(draft.descriptionKey).map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-slate-600 min-w-0">
        所見として表示する項目
        <select
          className={selectClass}
          style={{ maxWidth: '100%' }}
          value={draft.insightKey}
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, insightKey: e.target.value })}
        >
          <option value="">{inheritInsight}</option>
          {withCurrent(draft.insightKey).map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
